import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Avatar,
  AvatarFallback,
} from '@/components/atoms';
import { Tabs, TabsList, TabsTrigger } from '@/components/molecules';
import { ArrowLeftIcon, ArrowRightIcon, SearchIcon } from 'lucide-react';
import type { Contact } from '@/types';

interface ShareModalProps {
  showShareModal: boolean;
  setShowShareModal: (show: boolean) => void;
  onClose: () => void;
  onContactSelect: (contact: Contact) => void;
  contacts: Contact[];
}

export const ShareModal = ({
  showShareModal,
  setShowShareModal,
  onClose,
  onContactSelect,
  contacts,
}: ShareModalProps) => {
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [activeContactTab, setActiveContactTab] = useState('all');

  const handleClose = () => {
    onClose();
    setContactSearchQuery('');
    setActiveContactTab('all');
  };

  // Filter contacts based on search query and active tab
  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch =
      contact.name.toLowerCase().includes(contactSearchQuery.toLowerCase()) ||
      contact.lastMessage
        .toLowerCase()
        .includes(contactSearchQuery.toLowerCase());
    const matchesTab =
      activeContactTab === 'all' || contact.channel === activeContactTab;
    return matchesSearch && matchesTab;
  });

  return (
    <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
      <DialogContent className="max-w-lg w-full max-h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <ArrowLeftIcon className="w-4 h-4" />
            </Button>
            <DialogTitle>Share Receipt</DialogTitle>
            <span></span>
          </div>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0">
          {/* Search Bar */}
          <div className="p-4 flex justify-between items-center">
            <div className="flex-1 max-w-md">
              <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                <div className="flex items-center px-3 py-2 bg-gray-50">
                  <SearchIcon className="text-gray-400 w-4 h-4" />
                </div>
                <input
                  type="text"
                  placeholder="Search chat"
                  value={contactSearchQuery}
                  onChange={(e) => setContactSearchQuery(e.target.value)}
                  className="flex-1 outline-none bg-transparent px-3 py-2"
                />
              </div>
            </div>
          </div>

          {/* Channel Tabs */}
          <div className="p-4 border-b border-gray-200">
            <Tabs value={activeContactTab} onValueChange={setActiveContactTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
                <TabsTrigger value="instagram">Instagram</TabsTrigger>
                <TabsTrigger value="facebook">Facebook</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Contact List */}
          <div className="flex-1 overflow-y-auto">
            {filteredContacts.map((contact) => (
              <div
                key={contact.id}
                className="p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                onClick={() => onContactSelect(contact)}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-gray-200 text-gray-600">
                      {contact.initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900 truncate">
                        {contact.name}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {contact.time}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate mt-1">
                      {contact.lastMessage}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 capitalize">
                          {contact.channel}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* All Customers Button */}
          <div className="p-4 border-t border-gray-200">
            <Button
              variant="ghost"
              className="w-full flex items-center justify-center gap-2 text-gray-600"
              onClick={() => {
                alert('Opening all customers...');
                setShowShareModal(false);
              }}
            >
              All customers
              <ArrowRightIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
